import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { compressImage } from "../../firebase/upload";
import { FiUpload, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";

const defaultItems = [
  { title: "Music", image: "/selfcare/music.jpg", desc: "Listen to your favorite music. It\u2019s your soul\u2019s best friend." },
  { title: "Art", image: "/selfcare/art.jpg", desc: "Draw, paint or create. Being creative helps release stress and express feelings." },
  { title: "Yoga", image: "/selfcare/yoga.jpg", desc: "Free One\u2019s mind. Yoga creates balance and helps you feel calm and centered." },
  { title: "Gardening", image: "/selfcare/garden.jpg", desc: "Plant something and watch it grow. Gardening helps reduce stress and brings peace." },
  { title: "Physical Exercise", image: "/selfcare/physical.jpg", desc: "Move your body. Exercise supports mental health and improves energy, strength, and confidence." },
  { title: "Chats With Friends", image: "/selfcare/chat.jpg", desc: "Talk with friends and share your feelings. Connection helps reduce loneliness and stress." },
  { title: "Meditation", image: "/selfcare/meditation.jpg", desc: "Quiet the mind. Meditation improves focus, reduces stress, and supports inner calm." },
  { title: "Recommended Apps", image: "/selfcare/recomded.jpg", desc: "Try helpful apps for mindfulness, journaling, sleep, and mental wellness support." },
];

const defaultContent = {
  heroTitle: "SELF CARE",
  breadcrumb: "Home / Self-Care",
  selfCareItems: defaultItems,
  ctaHeading: "The Random Acts of Kindness Foundation | Welcome",
  ctaButtonText: "Visit Now",
  ctaButtonUrl: "",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

const SelfCareContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "selfCareContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev,
            ...data,
            selfCareItems: data.selfCareItems || defaultItems,
          }));
        }
      } catch (e) {
        if (e.code === "resource-exhausted" || (e.message && e.message.includes("exceeds"))) {
          alert("Document too large! Click 'Reset All Data' to clear old images and start fresh.");
        }
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleUpload = async (e, field, maxW = 600, quality = 0.5) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const base64 = await compressImage(file, maxW, quality);
      setContent((s) => ({ ...s, [field]: base64 }));
      await setDoc(doc(db, "settings", "selfCareContent"), { [field]: base64 }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleItemImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const key = `item-img-${index}`;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const base64 = await compressImage(file, 200, 0.3);
      const items = [...content.selfCareItems];
      items[index] = { ...items[index], image: base64 };
      setContent((s) => ({ ...s, selfCareItems: items }));
      await setDoc(doc(db, "settings", "selfCareContent"), { selfCareItems: items }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [key]: false }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "selfCareContent"), content, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message + "\n\nTry clicking 'Reset All Data' first to clear old large images."); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL self-care page data to defaults? This will remove all uploaded images and reset text fields.")) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "settings", "selfCareContent"));
      await setDoc(doc(db, "settings", "selfCareContent"), defaultContent);
      setContent(defaultContent);
      alert("Document reset successfully! You can now upload images with optimized compression.");
    } catch (e) { alert("Reset failed: " + e.message); }
    setSaving(false);
  };

  const updateItem = (i, field, val) => {
    const items = [...content.selfCareItems];
    items[i] = { ...items[i], [field]: val };
    setContent((s) => ({ ...s, selfCareItems: items }));
  };

  const addItem = () => {
    setContent((s) => ({ ...s, selfCareItems: [...s.selfCareItems, { title: "", image: "", desc: "" }] }));
  };

  const removeItem = (i) => {
    setContent((s) => ({ ...s, selfCareItems: s.selfCareItems.filter((_, idx) => idx !== i) }));
  };



  const UploadField = ({ label, field, maxW = 600, quality = 0.5 }) => (
    <div className="bg-gray-50 rounded-xl p-5">
      <label className="block text-[12px] font-bold text-gray-600 mb-3">{label}</label>
      {content[field] && (
        <img src={content[field]} alt={label} className="h-20 w-auto object-contain mb-3 bg-white rounded border p-1" />
      )}
      {!content[field] && (
        <div className="h-20 bg-gray-200 rounded mb-3 flex items-center justify-center text-[11px] text-gray-400 border-2 border-dashed">{label}</div>
      )}
      <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
        <FiUpload size={14} />
        {uploading[field] ? "Uploading..." : `Upload ${label}`}
        <input type="file" accept="image/*" onChange={(e) => handleUpload(e, field, maxW, quality)} className="hidden" disabled={uploading[field]} />
      </label>
    </div>
  );

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-black">Self-Care Page Content</h1>
          <p className="text-sm text-gray-400">Manage all content displayed on the Self-Care page</p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Hero */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Hero / Breadcrumb</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Hero Title</label>
                <input value={content.heroTitle} onChange={(e) => setContent((s) => ({ ...s, heroTitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Breadcrumb Text</label>
                <input value={content.breadcrumb} onChange={(e) => setContent((s) => ({ ...s, breadcrumb: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          {/* Self Care Items */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[14px] font-extrabold">Self Care Items</h2>
              <button type="button" onClick={addItem}
                className="flex items-center gap-1 text-[12px] text-[#e84fa5] font-semibold hover:text-pink-700 transition">
                <FiPlus size={14} /> Add Item
              </button>
            </div>
            <div className="space-y-5">
              {content.selfCareItems.map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-bold text-gray-400">Item {i + 1}</span>
                    {content.selfCareItems.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 transition">
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input value={item.title} onChange={(e) => updateItem(i, "title", e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Title" />
                    <textarea value={item.desc} onChange={(e) => updateItem(i, "desc", e.target.value)} rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5] resize-y" placeholder="Description" />
                    <div>
                      {item.image && (
                        <img src={item.image} alt="" className="h-14 w-auto object-contain mb-2 bg-white rounded border p-1" />
                      )}
                      <label className="flex items-center gap-2 cursor-pointer w-fit px-3 py-1.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[11px] text-gray-500 hover:text-[#e84fa5] transition">
                        <FiUpload size={12} />
                        {uploading[`item-img-${i}`] ? "..." : "Image"}
                        <input type="file" accept="image/*" onChange={(e) => handleItemImageUpload(e, i)} className="hidden" disabled={uploading[`item-img-${i}`]} />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">CTA Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.ctaHeading} onChange={(e) => setContent((s) => ({ ...s, ctaHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button Text</label>
                <input value={content.ctaButtonText} onChange={(e) => setContent((s) => ({ ...s, ctaButtonText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button URL</label>
                <input value={content.ctaButtonUrl} onChange={(e) => setContent((s) => ({ ...s, ctaButtonUrl: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Subscribe */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Subscribe Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.subscribeHeading} onChange={(e) => setContent((s) => ({ ...s, subscribeHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Input Placeholder</label>
                <input value={content.subscribePlaceholder} onChange={(e) => setContent((s) => ({ ...s, subscribePlaceholder: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button Text</label>
                <input value={content.subscribeButtonText} onChange={(e) => setContent((s) => ({ ...s, subscribeButtonText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Self-Care Page"}
            </button>
            {saved && (
              <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold">
                <FiCheck /> Self-Care page saved!
              </span>
            )}
            <button type="button" onClick={handleReset} disabled={saving}
              className="px-4 py-2 text-[11px] text-red-500 border border-red-300 rounded-lg hover:bg-red-50 transition font-semibold">
              Reset All Data
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SelfCareContent;
